import {
  ABILITY_SLOTS,
  CLASS_IDS,
  DATA,
  DIFFICULTY_TIERS,
  ITEM_IDS,
  ROOM_CONFIG,
  abilityBook,
  shopCost,
  inventoryContains,
  talentTree,
  nodeAbilityId,
  abilityActivated,
  xpToReach,
  xpToNext,
  ATTRIBUTE_IDS,
  type AbilitySlot,
  type AttributeId,
  type ClassId,
  type DifficultyTier,
  type ItemDef,
  type ItemStats,
  type TalentEffect,
} from '@gladiators/shared';
import type { PlayerView } from './net/state.js';

/**
 * Self-buffs surfaced in the HUD (feature 1.2, crit. 23; feature 1.3a, crit. 16;
 * feature 1.3b, crit. 17; feature 1.4, crit. 19).
 * Bulwark applies two underlying statuses (`armorBonus` + `damageReduction`);
 * `armorBonus` is the one shown. `slow` from Whirlwind's self-slow is not listed.
 * The `shield` row (Arcane Barrier) shows remaining absorb from `magnitude`
 * instead of a time; every other row shows the remaining seconds.
 * Cleric Blessing also grants `armorBonus`; the dedicated `blessing` marker
 * status disambiguates it from Bulwark (the Bulwark row hides while blessed).
 * Ranger Rapid Fire is keyed on `attackSpeedBonus` (unique to it; it also grants
 * `damageBonus` on the same timer). Cleric Sanctuary is keyed on
 * `damageReduction`; that status is also granted by Bulwark / Blessing, so the
 * Sanctuary row is suppressed while either of those is active. The `regen` row
 * is granted by both Druid Regrowth and Cleric Sanctuary, so it carries the
 * generic label "Regen". Sharing a status kind across abilities is the general
 * shortcoming here — see the "HUD buff bar" cleanup note in
 * `deferred-questions.md` 1.4.
 */
const HUD_BUFFS: [string, string][] = [
  ['armorBonus', 'Bulwark'],
  ['whirlwind', 'Whirlwind'],
  ['shield', 'Arcane Barrier'],
  ['blessing', 'Blessing'],
  ['regen', 'Regen'],
  ['attackSpeedBonus', 'Rapid Fire'],
  ['damageReduction', 'Sanctuary'],
];

/**
 * feature 5.1b — enemy-applied debuffs surfaced in the same HUD panel as the
 * buffs, with a distinct red-tinted `.debuff` row style. Each shows a remaining
 * countdown off the synced `PlayerView.statuses` entry (magnitude for `slow` is
 * a fraction, so the row shows time like the others). Kinds never collide with
 * `HUD_BUFFS`, so they share the `hud.buffs` map + render loop.
 */
const HUD_DEBUFFS: [string, string][] = [
  ['stun', 'Stunned'],
  ['slow', 'Slowed'],
  ['dot', 'Burning'],
];
const DEBUFF_KINDS = new Set(HUD_DEBUFFS.map(([k]) => k));

/** feature 5.5 — short HUD label for each difficulty tier (lobby picker +
 * in-match HUD label). */
const DIFFICULTY_LABEL: Record<DifficultyTier, string> = {
  easy: 'Easy',
  normal: 'Normal (Recommended)',
  medium: 'Medium',
  hard: 'Hard',
  brutal: 'Brutal',
};

/** feature 1.1 / 4.4 — short HUD label for each core attribute. */
const ATTR_LABEL: Record<AttributeId, string> = {
  strength: 'STR',
  agility: 'AGI',
  energy: 'ENE',
};

/** feature 3.1 — HUD label for each item stat key. */
const ITEM_STAT_LABEL: Record<keyof ItemStats, string> = {
  maxHp: 'HP',
  armor: 'Armor',
  damage: 'DMG',
  moveSpeed: 'Move Spd',
  strength: 'STR',
  agility: 'AGI',
  energy: 'ENE',
  attackSpeedPct: 'Atk Spd',
};

/** feature 3.1 — "+8 HP, +3 STR" one-line summary of an item's stat block. */
function itemStatSummary(stats: ItemStats): string {
  return (Object.keys(stats) as (keyof ItemStats)[])
    .map((k) =>
      k === 'attackSpeedPct'
        ? `+${Math.round((stats[k] ?? 0) * 100)}% ${ITEM_STAT_LABEL[k]}`
        : `+${stats[k]} ${ITEM_STAT_LABEL[k]}`,
    )
    .join(', ');
}

/** feature 4.5 — HUD letter for the ability slot a `skillBonus` item boosts. */
const ABILITY_SLOT_LABEL: Record<AbilitySlot, string> = { q: 'Q', w: 'W', e: 'E', r: 'R' };

/**
 * feature 3.1 / 4.5 — full one-line summary of an item: its stat block plus, for
 * a rare/epic "+1 to a skill" item, a `+1 to Q` clause (criterion 12).
 */
function itemDefSummary(def: ItemDef): string {
  const parts = [itemStatSummary(def.stats)].filter(Boolean);
  if (def.skillBonus)
    parts.push(`+${def.skillBonus.amount} to ${ABILITY_SLOT_LABEL[def.skillBonus.slot]}`);
  return parts.join(', ');
}

/** feature 4.3 — HUD label for each talent effect target. */
const TALENT_TARGET_LABEL: Record<string, string> = {
  damage: 'DMG',
  tickDamage: 'DMG',
  damagePct: 'DMG',
  heal: 'Healing',
  cooldownMs: 'Cooldown',
  manaCost: 'Mana',
  radius: 'Radius',
  range: 'Range',
  castRange: 'Range',
  maxRange: 'Range',
  durationMs: 'Duration',
  armorBonus: 'Armor',
  shieldAmount: 'Shield',
  regenPerTick: 'Regen',
  attackSpeedBonus: 'Atk Speed',
};

/** feature 4.3 — "+15% DMG" / "-12% Cooldown" one-line summary of a node effect. */
function talentEffectSummary(effect: TalentEffect): string {
  const label = TALENT_TARGET_LABEL[effect.target] ?? effect.target;
  if (effect.mode === 'addPct') {
    const pct = Math.round(effect.value * 100);
    return `${pct > 0 ? '+' : ''}${pct}% ${label}`;
  }
  return `${effect.value > 0 ? '+' : ''}${effect.value} ${label}`;
}

/** feature 3.1 — up-to-3-letter tag shown in a filled inventory slot. */
function itemAbbrev(name: string): string {
  const words = name.replace(/['’]/g, '').split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words.map((w) => w[0]).join('') : (words[0] ?? '');
  return letters.slice(0, 3).toUpperCase();
}

function rootEl(): HTMLDivElement {
  const el = document.getElementById('ui');
  if (!el) throw new Error('#ui container missing');
  return el as HTMLDivElement;
}

type Attrs = Record<string, string | number | boolean | ((e: Event) => void)>;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2), v as EventListener);
    } else if (k === 'class') {
      node.className = String(v);
    } else if (typeof v === 'boolean') {
      if (v) node.setAttribute(k, '');
    } else {
      node.setAttribute(k, String(v));
    }
  }
  for (const c of children) node.append(c);
  return node;
}

function setText(node: Node, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/**
 * The overlay is rebuilt only when its "view key" changes. `App.render()` runs
 * on every server patch (~20 Hz); without this guard the whole DOM — inputs and
 * buttons included — would be replaced 20×/s, making buttons unclickable.
 */
let currentKey: string | null = null;

function mountKeyed(key: string, build: () => Node): void {
  if (key === currentKey) return;
  currentKey = key;
  hud = null;
  rootEl().replaceChildren(build());
}

export function clearUi(): void {
  currentKey = null;
  hud = null;
  rootEl().replaceChildren();
}

// ---------------------------------------------------------------- start screen

export interface StartHandlers {
  onCreate: (name: string) => void;
  onJoin: (name: string, code: string) => void;
}

export function renderStart(h: StartHandlers, error = '', busy = false): void {
  mountKeyed(`start:${busy ? 1 : 0}:${error}`, () => {
    const name = el('input', {
      type: 'text',
      id: 'name',
      maxlength: 16,
      placeholder: 'Your name',
      value: sessionStorage.getItem('name') ?? '',
    });
    const code = el('input', {
      type: 'text',
      class: 'code',
      id: 'code',
      maxlength: 6,
      placeholder: 'ABC123',
    });
    const err = el('div', { class: 'error' }, [error]);

    const nameOf = (): string => (name.value || '').trim();
    const guard = (fn: () => void): void => {
      if (!nameOf()) {
        err.textContent = 'Enter a name first.';
        return;
      }
      sessionStorage.setItem('name', nameOf());
      fn();
    };

    const card = el('div', { class: 'card' }, [
      el('h1', {}, ['GLADIATORS']),
      el('h2', {}, ['Pilot — wave survival']),
      el('label', { for: 'name' }, ['Name']),
      name,
      el('div', { class: 'row' }, [
        el(
          'button',
          { class: 'primary', disabled: busy, onclick: () => guard(() => h.onCreate(nameOf())) },
          ['Create room'],
        ),
      ]),
      el('label', { for: 'code' }, ['Join with a 6-character code']),
      code,
      el('div', { class: 'row' }, [
        el(
          'button',
          {
            disabled: busy,
            onclick: () =>
              guard(() => {
                const c = (code.value || '').trim().toUpperCase();
                if (c.length !== 6) {
                  err.textContent = 'Codes are exactly 6 characters.';
                  return;
                }
                h.onJoin(nameOf(), c);
              }),
          },
          ['Join room'],
        ),
      ]),
      err,
      el('div', { class: 'hint' }, [
        busy ? 'Connecting…' : 'Click the ground to move · Q / W / E / R to cast abilities',
      ]),
    ]);

    return el('div', { class: 'overlay' }, [card]);
  });
}

// ---------------------------------------------------------------- lobby

export interface LobbyHandlers {
  onStart: () => void;
  onLeave: () => void;
  /** feature 5.5 — the host picks a difficulty tier (no-op for non-hosts; the
   * button row is non-interactive for them anyway). */
  onSetDifficulty: (tier: DifficultyTier) => void;
}

export function renderLobby(
  code: string,
  players: PlayerView[],
  isHost: boolean,
  difficulty: string,
  h: LobbyHandlers,
): void {
  const rosterKey = players.map((p) => `${p.id}~${p.name}~${p.host ? 'h' : ''}`).join('|');
  mountKeyed(`lobby:${code}:${isHost ? 1 : 0}:${players.length}:${rosterKey}:${difficulty}`, () => {
    const list = el(
      'ul',
      { class: 'players' },
      players.map((p) =>
        el('li', {}, [p.name, el('span', { class: 'tag' }, [p.host ? 'HOST' : ''])]),
      ),
    );
    // feature 5.5 (§C10) — 5 buttons, host-only clickable; a non-host sees the
    // current pick highlighted but the row is inert (no click handler wired).
    const diffButtons = DIFFICULTY_TIERS.map((tier) => {
      const btn = el(
        'button',
        {
          class: `diff-btn${tier === difficulty ? ' active' : ''}`,
          disabled: !isHost,
          onclick: () => h.onSetDifficulty(tier),
        },
        [DIFFICULTY_LABEL[tier]],
      );
      return btn;
    });
    const diffPicker = el('div', { class: 'diff-picker' }, [
      el('h2', {}, ['Difficulty']),
      el('div', { class: 'diff-row' }, diffButtons),
    ]);
    const card = el('div', { class: 'card' }, [
      el('h1', {}, ['LOBBY']),
      el('h2', {}, [`Share this code (${players.length}/${ROOM_CONFIG.MAX_PLAYERS} players)`]),
      el('div', { class: 'big-code' }, [code]),
      list,
      diffPicker,
      el('div', { class: 'row' }, [
        el('button', { onclick: () => h.onLeave() }, ['Leave']),
        el('button', { class: 'primary', disabled: !isHost, onclick: () => h.onStart() }, [
          isHost ? 'Start game' : 'Waiting for host…',
        ]),
      ]),
    ]);
    return el('div', { class: 'overlay' }, [card]);
  });
}

// ---------------------------------------------------------------- selection

export interface SelectHandlers {
  onSelect: (cls: ClassId) => void;
  onLock: () => void;
  onUnlock: () => void;
  onLeave: () => void;
}

// Persisted across re-renders so feedback text stays readable.
let selectNote = '';

export function renderSelect(
  players: PlayerView[],
  myId: string,
  countdown: number,
  phase: string,
  h: SelectHandlers,
): void {
  if (phase === 'countdown') selectNote = '';
  const me = players.find((p) => p.id === myId);
  const takenBy = new Map<string, string>();
  for (const p of players) if (p.locked && p.cls) takenBy.set(p.cls, p.name);

  const rosterKey = players
    .map((p) => `${p.id}~${p.name}~${p.cls}~${p.locked ? 'L' : ''}`)
    .join('|');
  const key = [
    'select',
    phase,
    Math.ceil(countdown),
    myId,
    me?.cls ?? '',
    me?.locked ? 1 : 0,
    selectNote,
    rosterKey,
  ].join(':');

  mountKeyed(key, () => {
    const note = el('div', { class: 'error' }, [selectNote]);
    const setNote = (msg: string): void => {
      selectNote = msg;
      note.textContent = msg;
    };

    const grid = el(
      'div',
      { class: 'classgrid' },
      CLASS_IDS.map((cls) => {
        const s = DATA.classes[cls];
        const taken = takenBy.has(cls) && me?.cls !== cls;
        const blockedByOwnLock = (me?.locked ?? false) && me?.cls !== cls;
        const classes = ['classcard'];
        if (me?.cls === cls) classes.push('sel');
        if (taken || blockedByOwnLock) classes.push('taken');
        const onclick = (): void => {
          if (taken) {
            setNote(`${s.name} is locked in by ${takenBy.get(cls)}. Pick another class.`);
          } else if (blockedByOwnLock) {
            setNote('Unlock first to change your class.');
          } else {
            setNote('');
            h.onSelect(cls);
          }
        };
        return el('button', { class: classes.join(' '), onclick }, [
          el('b', {}, [s.name]),
          `HP ${s.hp} · DMG ${s.damage}`,
          el('br', {}),
          `${s.attackStyle} · range ${s.attackRange}`,
          el('br', {}),
          taken ? el('span', { class: 'tag' }, [`locked: ${takenBy.get(cls)}`]) : '',
        ]);
      }),
    );

    const roster = el(
      'ul',
      { class: 'players' },
      players.map((p) =>
        el('li', {}, [
          p.name,
          el('span', { class: 'tag' }, [
            p.locked ? `LOCKED ${p.cls}` : p.cls ? `picking ${p.cls}` : '—',
          ]),
        ]),
      ),
    );

    const card = el('div', { class: 'card' }, [
      el('h1', {}, ['CHOOSE YOUR CLASS']),
      el('h2', {}, [
        phase === 'countdown'
          ? `All locked — starting in ${Math.ceil(countdown)}…`
          : 'Pick a class and lock in. The match starts when everyone is ready.',
      ]),
      grid,
      note,
      el('div', { class: 'row' }, [
        el('button', { onclick: () => h.onLeave() }, ['Leave']),
        me?.locked
          ? el('button', { onclick: () => h.onUnlock() }, ['Unlock'])
          : el('button', { class: 'primary', disabled: !me?.cls, onclick: () => h.onLock() }, [
              'Lock in',
            ]),
      ]),
      roster,
    ]);
    return el('div', { class: 'overlay' }, [card]);
  });
}

// ---------------------------------------------------------------- in-match HUD

export interface HudHandlers {
  onRestart: () => void;
  onQuit: () => void;
  onLeave: () => void;
  /** feature 2.2 — toggle the local player's wavebreak "Ready" vote. */
  onReady: () => void;
  /** feature 2.3 — buy one unit of the given shop catalog item. */
  onBuy: (itemId: string) => void;
  /** feature 3.1 — buy one item from the catalog into the first free slot. */
  onBuyItem: (itemId: string) => void;
  /** feature 3.1 — drop the item held in inventory slot `slot`. */
  onDropItem: (slot: number) => void;
  /** feature 3.3 — forge held items per the recipe with id `recipeId`. */
  onForge: (recipeId: string) => void;
  /** feature 4.2 — invest one skill point into ability `slot`. */
  onSpendAbilityPoint: (slot: AbilitySlot) => void;
  /** feature 4.3 — invest one skill point into the talent node `nodeId`. */
  onSpendTalentPoint: (nodeId: string) => void;
  /** feature 4.4 — spend one free attribute point on STR / AGI / ENE. */
  onAllocateAttr: (attr: AttributeId) => void;
  /** feature 4.2 — free wave-break respec: refund every invested ability point. */
  onRespec: () => void;
}

/** feature 2.2 — format a seconds countdown as `m:ss` (e.g. 177 -> "2:57"). */
function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

interface HudShell {
  key: string;
  name: HTMLElement;
  hp: HTMLElement;
  hpFill: HTMLElement;
  mp: HTMLElement;
  mpFill: HTMLElement;
  attrs: HTMLElement;
  /** feature 4.4 — per-attribute value cell + "+" allocate affordance, keyed by
   * attribute id. Built once; text + `.spendable` state patched every frame. */
  attrRows: Map<AttributeId, { val: HTMLElement; plus: HTMLButtonElement }>;
  /** feature 4.1 — "Lvl N" + unspent skill / attr point indicators. */
  level: HTMLElement;
  /** feature 4.1 — XP progress bar fill toward the next level. */
  xpFill: HTMLElement;
  goldxp: HTMLElement;
  ping: HTMLElement;
  wave: HTMLElement;
  /** feature 5.5 — small "Difficulty: X" label in the wave panel (patched in
   * place; the tier itself can't change mid-run, but this keeps the pattern
   * consistent with the rest of the shell). */
  difficultyLabel: HTMLElement;
  /** feature 5.6 — small "Revives left: N" indicator in the wave panel (patched
   * in place); hidden entirely (`style.display = 'none'`) when the active tier
   * has an unlimited budget (`revivalTokens` sentinel `-1`). */
  revivesLeft: HTMLElement;
  /** feature 2.2 — wavebreak timer / ready controls (patched in place). */
  waveTimer: HTMLElement;
  readyBtn: HTMLButtonElement;
  readyCount: HTMLElement;
  /** feature 2.3 — the wavebreak shop panel: rows built once from the static
   * catalog, cost / count / disabled patched each frame. */
  shop: {
    panel: HTMLElement;
    gold: HTMLElement;
    rows: Map<string, { cost: HTMLElement; count: HTMLElement; buy: HTMLButtonElement }>;
    /** feature 3.1 — the shop's Items section rows, keyed by catalog id. */
    items: Map<string, { cost: HTMLElement; buy: HTMLButtonElement }>;
    /** feature 3.3 — the shop's Forge section rows, keyed by recipe id
     * (craftability + Forge disabled state patched each frame). */
    forge: Map<string, { row: HTMLElement; btn: HTMLButtonElement }>;
    /** feature 4.2 — the free wave-break respec button (label + disabled patched). */
    respec: HTMLButtonElement;
    /** feature 4.3 — the TALENTS section: node rows keyed by node id (owned /
     * disabled state patched each frame). */
    talents: Map<string, { row: HTMLElement; btn: HTMLButtonElement }>;
    /** feature 4.3 — per-ability talent group containers keyed by ability id
     * (`.locked` toggled when the parent ability is not activated). */
    talentGroups: Map<string, HTMLElement>;
  };
  /** feature 3.1 — the always-visible 6-slot inventory grid (patched in place). */
  itemSlots: { root: HTMLElement; tag: HTMLElement }[];
  /** One entry per ability slot, ordered [q, w, e, r]. `mark` shows the feature
   * 4.2 lock / spend affordance marker. */
  abilities: { root: HTMLElement; cd: HTMLElement; mark: HTMLElement }[];
  /** Self-buff countdown timers, keyed by status kind (see HUD_BUFFS). */
  buffs: Map<string, { root: HTMLElement; time: HTMLElement }>;
  rows: Map<string, { row: HTMLElement; val: HTMLElement }>;
}

let hud: HudShell | null = null;

export function renderHud(
  me: PlayerView | undefined,
  players: PlayerView[],
  wave: number,
  phase: string,
  countdown: number,
  isHost: boolean,
  ping: number,
  difficulty: string,
  /** feature 5.6 — the shared wave-end revival-token budget; `-1` = unlimited
   * (hides the HUD indicator). */
  revivalTokens: number,
  h: HudHandlers,
): void {
  const rosterKey = players.map((p) => `${p.id}~${p.name}`).join('|');
  const shellKey = [
    'hud',
    phase === 'gameover' ? 'go' : phase === 'victory' ? 'win' : 'play',
    isHost ? 1 : 0,
    me?.id ?? '',
    me?.cls ?? '',
    rosterKey,
  ].join(':');

  currentKey = shellKey; // keep the keyed-mount guard in sync (HUD owns the DOM now)

  if (!hud || hud.key !== shellKey) {
    hud = buildHudShell(shellKey, players, isHost, phase, me?.cls ?? '', h);
  }

  const book = abilityBook((me?.cls || null) as ClassId | null);

  // ---- in-place dynamic updates (run every frame, no DOM teardown) ----
  setText(hud.name, me ? `${me.name} — ${me.cls}` : 'spectating');
  setText(hud.hp, `HP ${me?.hp ?? 0}/${me?.maxHp ?? 0}`);
  const frac = me && me.maxHp ? Math.max(0, Math.min(1, me.hp / me.maxHp)) : 0;
  hud.hpFill.style.width = `${(frac * 100).toFixed(1)}%`;

  const mana = Math.round(me?.mana ?? 0);
  const maxMana = Math.round(me?.maxMana ?? 0);
  setText(hud.mp, `MP ${mana}/${maxMana}`);
  const manaFrac = maxMana ? Math.max(0, Math.min(1, mana / maxMana)) : 0;
  hud.mpFill.style.width = `${(manaFrac * 100).toFixed(1)}%`;

  // feature 1.1 / 4.4 — the STR / AGI / ENE cells show the EFFECTIVE attribute
  // value (base + class growth + shop + items + allocated — `me.strength` etc.
  // are the post-recompute totals the server mirrors), with a small `(+N)` when
  // the player has spent free points into it. The "+" affordance is `.spendable`
  // (visible + clickable) only while the local player has an unspent free
  // attribute point. Patched in place — no DOM teardown (criterion 11).
  const canAllocate = !!me?.cls && (me?.freeAttrPoints ?? 0) > 0;
  const effAttr: Record<AttributeId, number> = {
    strength: me?.strength ?? 0,
    agility: me?.agility ?? 0,
    energy: me?.energy ?? 0,
  };
  const allocAttr: Record<AttributeId, number> = {
    strength: me?.allocatedAttr?.strength ?? 0,
    agility: me?.allocatedAttr?.agility ?? 0,
    energy: me?.allocatedAttr?.energy ?? 0,
  };
  for (const attr of ATTRIBUTE_IDS) {
    const cell = hud.attrRows.get(attr)!;
    const alloc = allocAttr[attr];
    setText(cell.val, `${effAttr[attr]}${alloc > 0 ? ` (+${alloc})` : ''}`);
    cell.plus.classList.toggle('spendable', canAllocate);
    cell.plus.disabled = !canAllocate;
  }

  // feature 4.1 — level, an XP progress bar toward the next level, and the
  // unspent skill / attr point indicators (shown only when > 0 — the affordances
  // 4.2 / 4.4 make interactive). Patched in place, no DOM teardown (criterion 13).
  const lvl = me?.level ?? 1;
  const skillPts = me?.skillPoints ?? 0;
  const attrPts = me?.freeAttrPoints ?? 0;
  const pips = [
    skillPts > 0 ? `● ${skillPts} skill pt${skillPts === 1 ? '' : 's'}` : '',
    attrPts > 0 ? `● ${attrPts} attr pt${attrPts === 1 ? '' : 's'}` : '',
  ]
    .filter(Boolean)
    .join('   ');
  setText(hud.level, `Lvl ${lvl}${pips ? `   ${pips}` : ''}`);
  hud.level.classList.toggle('has-points', skillPts > 0 || attrPts > 0);
  const xpFloor = xpToReach(lvl);
  const xpSpan = xpToNext(lvl);
  const xpFrac = xpSpan > 0 ? Math.max(0, Math.min(1, ((me?.xp ?? 0) - xpFloor) / xpSpan)) : 1;
  hud.xpFill.style.width = `${(xpFrac * 100).toFixed(1)}%`;

  // feature 4.2 — an ability with no point invested is LOCKED (greyed + a lock
  // glyph, cannot be cast). With an unspent skill point, an un-activated slot
  // shows a "+" spend affordance that sends `spendAbilityPoint`. The `r` slot
  // also shows a locked-until-level marker while below `ultLevelGate` and
  // un-activated.
  //
  // feature 4.5 — a held "+1 <slot>" item activates a slot with no point spent:
  // `activated` (drives the lock glyph / greying) reads EFFECTIVE points
  // (`abilityActivated` = spent points + held `+1` items). The "+" spend
  // affordance still keys off zero SPENT points (`invested === 0`) — spending a
  // real point remains useful for talents even once an item activated the slot.
  const ultGate = DATA.progression.ultLevelGate;
  for (let i = 0; i < hud.abilities.length; i++) {
    const slot = ABILITY_SLOTS[i]!;
    const def = book[slot];
    const cdMs = me?.abilityCd?.[i] ?? 0;
    const cooling = cdMs > 0;
    const noMana = !!me && me.mana < def.manaCost;
    const invested = me?.abilityPoints?.[slot] ?? 0;
    const activated = me ? abilityActivated(me, slot) : invested >= 1;
    // feature 4.5 — activated with zero SPENT points means a held `+1 <slot>`
    // item is doing it (the ability IS castable).
    const itemActivated = activated && invested === 0;
    // The ability genuinely cannot be cast: no spent point, no item, and — for
    // `r` — below the ult level.
    const ultLocked = slot === 'r' && !activated && lvl < ultGate;
    // The FIRST *spent* point in `r` is still gated by the ult level even when a
    // `+1 R` item already made it castable, so the `+` affordance stays hidden.
    const ultSpendGated = slot === 'r' && invested === 0 && lvl < ultGate;
    // TODO(4.3): re-enable +N for talent-point investment on activated abilities.
    // In 4.2 a point past the first does nothing, so only the activation click
    // (no SPENT point yet) gets the `.spendable` affordance — the server
    // `spendAbilityPoint` keeps its `< abilityPointCap` check for 4.3.
    const spendable = !!me?.cls && skillPts > 0 && invested === 0 && !ultSpendGated;
    const entry = hud.abilities[i]!;
    setText(entry.cd, cooling ? `${Math.ceil(cdMs / 1000)}` : '');
    entry.root.classList.toggle('cooling', cooling);
    entry.root.classList.toggle('nomana', noMana && !cooling && activated);
    entry.root.classList.toggle('locked', !activated);
    entry.root.classList.toggle('spendable', spendable);
    entry.root.classList.toggle('ult-locked', ultLocked);
    setText(entry.mark, ultLocked ? `Lv${ultGate}` : spendable ? '+' : !activated ? '🔒' : '');
    entry.root.title = ultLocked
      ? `${def.name} — unlocks at level ${ultGate}`
      : itemActivated
        ? `${def.name} — active (via item)`
        : !activated
          ? `${def.name} — locked (spend a skill point to activate)`
          : def.name;
  }

  // Active self-buffs (Bulwark / Whirlwind / Arcane Barrier / Blessing / Regen /
  // Rapid Fire / Sanctuary), patched in place.
  const active = (k: string): boolean =>
    !!me?.statuses?.some((s) => s.kind === k && s.remainingMs > 0);
  const blessed = active('blessing');
  for (const [kind, entry] of hud.buffs) {
    // feature 5.1b — a debuff row shows only for an ENEMY-sourced status; a
    // hero's own slow/stun/dot (Whirlwind's self-slow, `sourceId === me.id`) is
    // not a threat and must not trip the row.
    const st = DEBUFF_KINDS.has(kind)
      ? me?.statuses?.find((s) => s.kind === kind && s.sourceId !== me?.id)
      : me?.statuses?.find((s) => s.kind === kind);
    // While blessed, the Bulwark row hides so the Blessing row owns the
    // `armorBonus` display (Blessing grants `armorBonus` too). The Sanctuary row
    // (`damageReduction`) hides while Bulwark / Blessing — which also grant
    // `damageReduction` — are up, so it never mislabels their mitigation.
    const suppressed =
      (kind === 'armorBonus' && blessed) ||
      (kind === 'damageReduction' && (active('armorBonus') || blessed));
    if (st && st.remainingMs > 0 && !suppressed) {
      entry.root.style.display = '';
      setText(
        entry.time,
        kind === 'shield'
          ? `${Math.max(0, Math.ceil(st.magnitude))}`
          : `${(st.remainingMs / 1000).toFixed(1)}s`,
      );
    } else {
      entry.root.style.display = 'none';
    }
  }

  setText(hud.goldxp, `Gold ${me?.gold ?? 0}   XP ${me?.xp ?? 0}`);
  setText(hud.ping, `ping ~${Math.round(ping)}ms`);
  // feature 5.5 (§C11) — small active-difficulty label in the wave panel.
  setText(
    hud.difficultyLabel,
    `Difficulty: ${DIFFICULTY_LABEL[difficulty as DifficultyTier] ?? difficulty}`,
  );
  // feature 5.6 (§C8/C9) — "Revives left: N", hidden entirely on an unlimited
  // tier (sentinel -1). Built once in the shell above; only visibility + text
  // are patched here.
  if (revivalTokens >= 0) {
    hud.revivesLeft.style.display = '';
    setText(hud.revivesLeft, `Revives left: ${revivalTokens}`);
  } else {
    hud.revivesLeft.style.display = 'none';
  }

  // feature 2.2 — during a wavebreak the wave line becomes the shop window: an
  // mm:ss countdown, a Ready / Unready toggle, and a ready-vote tally. Outside
  // the break it is just "Wave N". Everything is patched in place.
  if (phase === 'wavebreak') {
    const label = wave > 0 ? `Wave ${wave} cleared` : 'Get ready';
    setText(hud.wave, `${label} — next wave in ${mmss(countdown)}`);
    const voters = players.filter((p) => p.cls);
    const readyN = voters.filter((p) => p.ready).length;
    setText(hud.readyCount, `${readyN} / ${voters.length} ready`);
    setText(hud.readyBtn, me?.ready ? 'Unready' : 'Ready');
    hud.readyBtn.classList.toggle('active', !!me?.ready);
    hud.readyBtn.disabled = !me?.cls;
    hud.waveTimer.style.display = '';
  } else {
    setText(hud.wave, `Wave ${wave}`);
    setText(hud.readyCount, '');
    // Reset the hidden toggle so a later wavebreak never flashes a stale "Unready".
    setText(hud.readyBtn, 'Ready');
    hud.readyBtn.classList.remove('active');
    hud.waveTimer.style.display = 'none';
  }

  for (const p of players) {
    const entry = hud.rows.get(p.id);
    if (!entry) continue;
    setText(entry.val, p.dead ? 'DEAD' : `${p.hp}`);
    entry.row.classList.toggle('dead', p.dead);
  }

  // feature 2.3 — the shop panel is visible only during `wavebreak` (criteria
  // 14 / 15). The rows were built once from the static catalog; here we only
  // patch the cost text, the bought-count text and each Buy button's disabled
  // state from `me`'s gold + purchase counts (criterion 16 — no DOM teardown).
  if (phase === 'wavebreak') {
    hud.shop.panel.style.display = '';
    const gold = me?.gold ?? 0;
    setText(hud.shop.gold, `Gold ${gold}`);
    // feature 4.2 / 4.3 / 4.4 — respec refunds every invested ability point,
    // owned talent node AND spent free attribute point; the label shows the
    // total, and the button is disabled when there is nothing to refund.
    const refundable = me
      ? me.abilityPoints.q +
        me.abilityPoints.w +
        me.abilityPoints.e +
        me.abilityPoints.r +
        me.talentNodes.length +
        me.allocatedAttr.strength +
        me.allocatedAttr.agility +
        me.allocatedAttr.energy
      : 0;
    setText(hud.shop.respec, refundable > 0 ? `Respec (refund ${refundable})` : 'Respec');
    hud.shop.respec.disabled = !me?.cls || refundable < 1;
    for (const item of DATA.shop) {
      const entry = hud.shop.rows.get(item.id);
      if (!entry) continue;
      const bought = me?.shopBuys?.[item.id] ?? 0;
      const maxed = bought >= item.maxBuys;
      const cost = shopCost(item, bought);
      setText(entry.cost, maxed ? 'MAX' : `${cost}g`);
      setText(entry.count, `${bought}/${item.maxBuys}`);
      entry.buy.disabled = !me?.cls || maxed || gold < cost;
    }
    // feature 3.1 — the shop's Items section: every catalog item, Buy disabled on
    // unaffordable or a full inventory (criterion 15).
    const inv = me?.inventory ?? [];
    const invFull = inv.filter((s) => s).length >= 6;
    for (const id of ITEM_IDS) {
      const entry = hud.shop.items.get(id);
      if (!entry) continue;
      const def = DATA.items[id]!;
      setText(entry.cost, `${def.cost}g`);
      entry.buy.disabled = !me?.cls || invFull || gold < def.cost;
    }
    // feature 3.3 — the FORGE section: a recipe's Forge button is enabled only
    // while the player currently holds every input (matching multiplicity); an
    // un-craftable recipe's row is greyed. Rows were built once (criterion 11).
    for (const recipe of DATA.recipes) {
      const entry = hud.shop.forge.get(recipe.id);
      if (!entry) continue;
      const craftable = !!me?.cls && inventoryContains(inv, recipe.inputs);
      entry.btn.disabled = !craftable;
      entry.row.classList.toggle('uncraftable', !craftable);
    }
    // feature 4.3 — the TALENTS section: for each ability, its ~3 nodes. Buy is
    // disabled when the node is owned, the parent ability is un-activated, the
    // ability is at the point cap, or the player has no spare skill point; an
    // un-activated ability's group is greyed. Rows were built once (criterion 15).
    const owned = me?.talentNodes ?? [];
    const cap = DATA.progression.abilityPointCap;
    for (const slot of ABILITY_SLOTS) {
      const def = book[slot];
      const invested = me?.abilityPoints?.[slot] ?? 0;
      // feature 4.5 — a held `+1 <slot>` item activates the tree's parent
      // ability, matching the server `spendTalentPoint` gate. The cap still
      // counts only SPENT points + owned nodes (`invested`), not the item bonus.
      const activated = me ? abilityActivated(me, slot) : invested >= 1;
      const ownedHere = owned.filter((n) => nodeAbilityId(n) === def.id).length;
      const atCap = invested + ownedHere >= cap;
      hud.shop.talentGroups.get(def.id)?.classList.toggle('locked', !activated);
      for (const node of talentTree(def.id)) {
        const entry = hud.shop.talents.get(node.id);
        if (!entry) continue;
        const has = owned.includes(node.id);
        entry.row.classList.toggle('owned', has);
        setText(entry.btn, has ? 'Owned' : 'Buy');
        entry.btn.disabled = !me?.cls || !activated || has || atCap || (me?.skillPoints ?? 0) < 1;
      }
    }
  } else {
    hud.shop.panel.style.display = 'none';
  }

  // feature 3.1 — the 6-slot inventory grid: always visible in match / wavebreak
  // (criterion 14). Each slot shows the held item's rarity colour + abbreviation
  // or an empty marker; during `wavebreak` a filled slot is clickable to drop
  // (criterion 16), otherwise it is display-only (`.droppable` gates the click).
  for (let i = 0; i < hud.itemSlots.length; i++) {
    const entry = hud.itemSlots[i]!;
    const id = me?.inventory?.[i] ?? '';
    const def = id ? DATA.items[id] : undefined;
    setText(entry.tag, def ? itemAbbrev(def.name) : '');
    entry.root.className =
      'item-slot' +
      (def ? ` filled rarity-${def.rarity}` : '') +
      (def && phase === 'wavebreak' ? ' droppable' : '');
    entry.root.title = def
      ? `${def.name} — ${itemDefSummary(def)}${phase === 'wavebreak' ? ' (click to drop)' : ''}`
      : 'Empty slot';
  }
}

function buildHudShell(
  key: string,
  players: PlayerView[],
  isHost: boolean,
  phase: string,
  meCls: string,
  h: HudHandlers,
): HudShell {
  const name = el('div');
  const hp = el('div');
  const hpFill = el('i');
  const mp = el('div');
  const mpFill = el('i');
  // feature 1.1 / 4.4 — the STR / AGI / ENE line. One cell per attribute, built
  // once; each cell carries a "+" allocate button whose `.spendable` state is
  // patched every frame (shown only when the local player has a free attribute
  // point). Clicks are wired once — never rebuilt, same rule as the ability row.
  const attrs = el('div', { class: 'attrs' });
  const attrRows = new Map<AttributeId, { val: HTMLElement; plus: HTMLButtonElement }>();
  for (const attr of ATTRIBUTE_IDS) {
    const val = el('span', { class: 'attr-val' });
    const plus = el('button', { class: 'attr-plus' }) as HTMLButtonElement;
    plus.textContent = '+';
    plus.addEventListener('click', () => {
      if (plus.classList.contains('spendable')) h.onAllocateAttr(attr);
    });
    attrRows.set(attr, { val, plus });
    attrs.append(
      el('span', { class: 'attr' }, [
        el('span', { class: 'attr-label' }, [ATTR_LABEL[attr]]),
        val,
        plus,
      ]),
    );
  }
  const level = el('div', { class: 'level-line' });
  const xpFill = el('i');
  const goldxp = el('div');
  const ping = el('div', { class: 'hint' });
  const stats = el('div', { class: 'hud-panel', id: 'hud-stats' }, [
    name,
    hp,
    el('div', { class: 'bar' }, [hpFill]),
    mp,
    el('div', { class: 'bar mana' }, [mpFill]),
    attrs,
    level,
    el('div', { class: 'bar xp' }, [xpFill]),
    goldxp,
    ping,
  ]);

  // Ability row (Q / W / E / R). Built once; the cooldown / mana state is
  // patched in place every frame (never rebuilt — that would break clicks).
  const abilities: { root: HTMLElement; cd: HTMLElement; mark: HTMLElement }[] = [];
  const abilityRow = el('div', { class: 'hud-panel', id: 'hud-abilities' });
  const book = abilityBook((meCls || null) as ClassId | null);
  for (const slot of ABILITY_SLOTS) {
    const def = book[slot];
    const cd = el('span', { class: 'cd' });
    // feature 4.2 — lock / spend-affordance marker, patched in place per frame.
    const mark = el('span', { class: 'ability-mark' });
    const root = el('div', { class: 'ability' }, [
      el('span', { class: 'key' }, [slot.toUpperCase()]),
      el('span', { class: 'aname' }, [def.name]),
      cd,
      mark,
    ]);
    // feature 4.2 — a click spends a skill point only while the slot carries the
    // `.spendable` affordance (set by renderHud iff the local player has a spare
    // point and the slot is un-activated / under cap). Attached once — never
    // rebuilt, same rule as the shop / ready buttons (criterion 16).
    root.addEventListener('click', () => {
      if (root.classList.contains('spendable')) h.onSpendAbilityPoint(slot);
    });
    abilities.push({ root, cd, mark });
    abilityRow.append(root);
  }

  // Self-buff countdown timers (built once, shown/hidden in place per frame).
  const buffs = new Map<string, { root: HTMLElement; time: HTMLElement }>();
  const buffPanel = el('div', { class: 'hud-panel', id: 'hud-buffs' });
  const addBuffRow = (kind: string, label: string, cls: string): void => {
    const time = el('span', { class: 'buff-time' });
    const root = el('div', { class: cls }, [el('span', { class: 'buff-name' }, [label]), time]);
    root.style.display = 'none';
    buffs.set(kind, { root, time });
    buffPanel.append(root);
  };
  for (const [kind, label] of HUD_BUFFS) addBuffRow(kind, label, 'buff');
  // feature 5.1b — enemy debuffs share the panel with a red-tinted row style.
  for (const [kind, label] of HUD_DEBUFFS) addBuffRow(kind, label, 'buff debuff');

  // feature 2.2 — wave line + wavebreak controls. Built once; the Ready button is
  // a persistent node whose label / state is patched each frame (never rebuilt —
  // that would break clicks, same rule as the ability row).
  const wave = el('div', { class: 'wave-name' });
  // feature 5.5 (§C11) — small active-difficulty label in the wave panel.
  const difficultyLabel = el('div', { class: 'difficulty-label' });
  // feature 5.6 (§C8/C9) — small "Revives left: N" indicator, hidden entirely
  // on an unlimited tier; visibility + text are patched in `renderHud`.
  const revivesLeft = el('div', { class: 'revives-left' });
  revivesLeft.style.display = 'none';
  const readyBtn = el('button', {
    class: 'ready-btn',
    onclick: () => h.onReady(),
  }) as HTMLButtonElement;
  readyBtn.textContent = 'Ready';
  const readyCount = el('span', { class: 'ready-count' });
  const waveTimer = el('div', { class: 'wave-timer' }, [readyBtn, readyCount]);
  waveTimer.style.display = 'none';
  const wavePanel = el('div', { class: 'hud-panel', id: 'hud-wave' }, [
    wave,
    difficultyLabel,
    revivesLeft,
    waveTimer,
  ]);

  // feature 2.3 — shop panel. Built once from the static `DATA.shop` catalog
  // (one row per item, catalog order); the cost / count / disabled state is
  // patched in place each frame, never rebuilt (same rule as the ability row).
  const shopRows = new Map<
    string,
    { cost: HTMLElement; count: HTMLElement; buy: HTMLButtonElement }
  >();
  const shopGold = el('div', { class: 'shop-gold' });
  const shopPanel = el('div', { class: 'hud-panel', id: 'hud-shop' }, [
    el('div', { class: 'shop-title' }, ['SHOP']),
    shopGold,
  ]);

  // feature 4.2 — the free wave-break respec. Built once; the label (refundable
  // point count) + disabled state are patched each frame. Not a `.shop-row` so
  // the catalog-row selectors / counts are unaffected.
  const respecBtn = el('button', {
    class: 'respec-btn',
    onclick: () => h.onRespec(),
  }) as HTMLButtonElement;
  respecBtn.textContent = 'Respec';
  shopPanel.append(el('div', { class: 'respec-row' }, [respecBtn]));

  for (const item of DATA.shop) {
    const cost = el('span', { class: 'shop-cost' });
    const count = el('span', { class: 'shop-count' });
    const buy = el('button', {
      class: 'shop-buy',
      onclick: () => h.onBuy(item.id),
    }) as HTMLButtonElement;
    buy.textContent = 'Buy';
    const row = el('div', { class: 'shop-row' }, [
      el('span', { class: 'shop-label' }, [`${item.label} +${item.amountPerBuy}`]),
      cost,
      count,
      buy,
    ]);
    shopRows.set(item.id, { cost, count, buy });
    shopPanel.append(row);
  }

  // feature 3.1 — the Items section: one row per catalog item (declaration
  // order), built once; only the cost text + Buy disabled state are patched.
  const shopItemRows = new Map<string, { cost: HTMLElement; buy: HTMLButtonElement }>();
  shopPanel.append(el('div', { class: 'shop-subtitle' }, ['ITEMS']));
  for (const id of ITEM_IDS) {
    const def = DATA.items[id]!;
    const cost = el('span', { class: 'shop-cost' });
    const buy = el('button', {
      class: 'shop-buy',
      onclick: () => h.onBuyItem(id),
    }) as HTMLButtonElement;
    buy.textContent = 'Buy';
    const row = el('div', { class: `shop-row item rarity-${def.rarity}` }, [
      el('span', { class: 'shop-label' }, [
        el('b', {}, [def.name]),
        el('span', { class: 'shop-item-stats' }, [itemDefSummary(def)]),
      ]),
      cost,
      buy,
    ]);
    shopItemRows.set(id, { cost, buy });
    shopPanel.append(row);
  }

  // feature 3.3 — the FORGE section: one row per recipe (declaration order),
  // built once. Each row shows the output (name + stat summary), the inputs it
  // consumes and a Forge button; only the craftability (button disabled + row
  // greyed) is patched each frame (no per-frame DOM teardown — criterion 11).
  const shopForgeRows = new Map<string, { row: HTMLElement; btn: HTMLButtonElement }>();
  if (DATA.recipes.length > 0) {
    shopPanel.append(el('div', { class: 'shop-subtitle' }, ['FORGE']));
    for (const recipe of DATA.recipes) {
      const out = DATA.items[recipe.output]!;
      const btn = el('button', {
        class: 'forge-btn',
        onclick: () => h.onForge(recipe.id),
      }) as HTMLButtonElement;
      btn.textContent = 'Forge';
      const inputs = recipe.inputs.map((i) => DATA.items[i]?.name ?? i).join(' + ');
      const row = el('div', { class: `shop-row forge rarity-${out.rarity}` }, [
        el('span', { class: 'shop-label' }, [
          el('b', {}, [out.name]),
          el('span', { class: 'shop-item-stats' }, [itemDefSummary(out)]),
          el('span', { class: 'forge-inputs' }, [`uses ${inputs}`]),
        ]),
        btn,
      ]);
      shopForgeRows.set(recipe.id, { row, btn });
      shopPanel.append(row);
    }
  }

  // feature 4.3 — the TALENTS section: one group per local-class ability, each
  // with its ~3 talent-node rows (name + effect summary + Buy). Built once from
  // the static `DATA.talents` (the node set never changes); per frame only the
  // owned / disabled / group-locked state is patched (criterion 15).
  const shopTalentRows = new Map<string, { row: HTMLElement; btn: HTMLButtonElement }>();
  const shopTalentGroups = new Map<string, HTMLElement>();
  {
    let anyTree = false;
    const groups: Node[] = [];
    for (const slot of ABILITY_SLOTS) {
      const def = book[slot];
      const nodes = talentTree(def.id);
      if (nodes.length === 0) continue;
      anyTree = true;
      const group = el('div', { class: 'talent-group' }, [
        el('div', { class: 'talent-ability' }, [`${slot.toUpperCase()} · ${def.name}`]),
      ]);
      for (const node of nodes) {
        const btn = el('button', {
          class: 'talent-buy',
          onclick: () => h.onSpendTalentPoint(node.id),
        }) as HTMLButtonElement;
        btn.textContent = 'Buy';
        const row = el('div', { class: 'shop-row talent' }, [
          el('span', { class: 'shop-label' }, [
            el('b', {}, [node.name]),
            el('span', { class: 'shop-item-stats' }, [talentEffectSummary(node.effect)]),
          ]),
          btn,
        ]);
        shopTalentRows.set(node.id, { row, btn });
        group.append(row);
      }
      shopTalentGroups.set(def.id, group);
      groups.push(group);
    }
    if (anyTree) {
      shopPanel.append(el('div', { class: 'shop-subtitle' }, ['TALENTS']));
      for (const g of groups) shopPanel.append(g);
    }
  }

  shopPanel.style.display = 'none';

  // feature 3.1 — the always-visible 6-slot inventory grid. Slots are built once;
  // the held item / rarity class is patched each frame. A click only drops when
  // the slot is currently `.droppable` (set by `renderHud` iff the slot is
  // filled AND `phase === 'wavebreak'`), so the grid is display-only otherwise
  // and no `dropItem` message is sent for an empty slot or during match/gameover
  // (the CSS `pointer-events` gate is a second line of defence).
  const itemSlots: { root: HTMLElement; tag: HTMLElement }[] = [];
  const itemGrid = el('div', { class: 'hud-panel', id: 'hud-items' });
  for (let i = 0; i < 6; i++) {
    const tag = el('span', { class: 'item-tag' });
    const root = el('div', { class: 'item-slot' }, [tag]);
    root.addEventListener('click', () => {
      if (root.classList.contains('droppable')) h.onDropItem(i);
    });
    itemSlots.push({ root, tag });
    itemGrid.append(root);
  }

  const rows = new Map<string, { row: HTMLElement; val: HTMLElement }>();
  const rosterPanel = el('div', { class: 'hud-panel', id: 'hud-players' });
  for (const p of players) {
    const val = el('span');
    const row = el('div', {}, [el('span', {}, [p.name]), val]);
    rows.set(p.id, { row, val });
    rosterPanel.append(row);
  }

  const layers: Node[] = [
    stats,
    abilityRow,
    buffPanel,
    wavePanel,
    shopPanel,
    itemGrid,
    rosterPanel,
  ];

  if (phase === 'gameover') {
    layers.push(el('div', { class: 'banner' }, ['GAME OVER']));
    layers.push(
      el('div', { class: 'hud-panel', id: 'hud-gameover' }, [
        isHost
          ? el('div', { class: 'row' }, [
              el('button', { class: 'primary', onclick: () => h.onRestart() }, ['Restart']),
              el('button', { class: 'danger', onclick: () => h.onQuit() }, ['Quit room']),
            ])
          : el('div', {}, ['Waiting for the host to restart or quit…']),
      ]),
    );
  }

  // feature 5.4 — the wave-30 victory screen, built once per shell key
  // (mirrors the `gameover` panel above). By the time the server flips the
  // phase to `victory`, `applyLevelUps` has already settled every player's
  // final level for the run, so a static, build-once list is fine (criterion 9).
  if (phase === 'victory') {
    layers.push(el('div', { class: 'banner victory' }, ['VICTORY']));
    const statsRows = players.map((p) =>
      el('div', { class: 'run-stat-row' }, [
        el('span', { class: 'run-stat-name' }, [p.name]),
        el('span', { class: 'run-stat-level' }, [`Lv ${p.level}`]),
      ]),
    );
    layers.push(
      el('div', { class: 'hud-panel', id: 'hud-victory' }, [
        el('div', {}, [`${DATA.bosses.arenaMaster?.name ?? 'The Arena Master'} has fallen.`]),
        el('div', { class: 'run-stats' }, statsRows),
        isHost
          ? el('div', { class: 'row' }, [
              el('button', { class: 'primary', onclick: () => h.onRestart() }, ['Restart']),
              el('button', { class: 'danger', onclick: () => h.onQuit() }, ['Quit room']),
            ])
          : el('div', {}, ['Waiting for the host to restart or quit…']),
      ]),
    );
  }

  rootEl().replaceChildren(el('div', { id: 'hud' }, layers));
  return {
    key,
    name,
    hp,
    hpFill,
    mp,
    mpFill,
    attrs,
    attrRows,
    level,
    xpFill,
    goldxp,
    ping,
    wave,
    difficultyLabel,
    revivesLeft,
    waveTimer,
    readyBtn,
    readyCount,
    shop: {
      panel: shopPanel,
      gold: shopGold,
      rows: shopRows,
      items: shopItemRows,
      forge: shopForgeRows,
      respec: respecBtn,
      talents: shopTalentRows,
      talentGroups: shopTalentGroups,
    },
    itemSlots,
    abilities,
    buffs,
    rows,
  };
}

// ---------------------------------------------------------------- message

export function renderMessage(title: string, body: string, onBack: () => void): void {
  mountKeyed(`msg:${title}:${body}`, () =>
    el('div', { class: 'overlay' }, [
      el('div', { class: 'card' }, [
        el('h1', {}, [title]),
        el('h2', {}, [body]),
        el('div', { class: 'row' }, [
          el('button', { class: 'primary', onclick: () => onBack() }, ['Back to start']),
        ]),
      ]),
    ]),
  );
}
